import { Camera, Check, LogOut, Moon, Settings, Sparkles, X } from 'lucide-react';
import clsx from 'clsx';
import { useEffect, useState } from 'react';
import type { BrowserNotificationState } from '../hooks/useNotificationPermission';
import type { Profile } from '../types';
import { uploadAvatar } from '../lib/storage';
import Avatar from './Avatar';

export type RelayTheme = 'midnight' | 'cobalt' | 'graphite' | 'amoled';

export interface RelayPreferences {
  theme: RelayTheme;
  compactMode: boolean;
  glassPanels: boolean;
  reduceMotion: boolean;
}

const themes: Array<{ id: RelayTheme; label: string; copy: string; swatch: string }> = [
  { id: 'midnight', label: 'Midnight', copy: 'Balanced charcoal with cyan accents.', swatch: 'from-slate-950 to-cyan-500' },
  { id: 'cobalt', label: 'Cobalt', copy: 'Cool blue-gray surfaces and glow.', swatch: 'from-blue-950 to-blue-500' },
  { id: 'graphite', label: 'Graphite', copy: 'Low-glow neutral professional mode.', swatch: 'from-zinc-950 to-zinc-500' },
  { id: 'amoled', label: 'Amoled', copy: 'Near-black for OLED displays.', swatch: 'from-black to-slate-700' },
];

export default function SettingsModal({
  profile,
  preferences,
  notificationPermission,
  onClose,
  onPreferencesChange,
  onUpdateProfile,
  onRequestNotifications,
  onSignOut,
}: {
  profile: Profile;
  preferences: RelayPreferences;
  notificationPermission: BrowserNotificationState;
  onClose: () => void;
  onPreferencesChange: (preferences: RelayPreferences) => void;
  onUpdateProfile: (updates: Partial<Pick<Profile, 'display_name' | 'username' | 'bio' | 'avatar_url'>>) => Promise<void>;
  onRequestNotifications: () => void;
  onSignOut: () => void;
}) {
  const [displayName, setDisplayName] = useState(profile.display_name);
  const [username, setUsername] = useState(profile.username);
  const [bio, setBio] = useState(profile.bio || '');
  const [message, setMessage] = useState<string | null>(null);
  const [saving, setSaving] = useState(false);

  useEffect(() => {
    function handleKeyDown(event: KeyboardEvent) {
      if (event.key === 'Escape') onClose();
    }

    window.addEventListener('keydown', handleKeyDown);
    return () => window.removeEventListener('keydown', handleKeyDown);
  }, [onClose]);

  function updatePreference(updates: Partial<RelayPreferences>) {
    onPreferencesChange({ ...preferences, ...updates });
  }

  async function saveProfile() {
    setSaving(true);
    setMessage(null);
    try {
      await onUpdateProfile({
        display_name: displayName.trim(),
        username: username.trim(),
        bio: bio.trim() || null,
      });
      setMessage('Profile saved.');
    } catch (error) {
      setMessage(error instanceof Error ? error.message : 'Could not save profile.');
    } finally {
      setSaving(false);
    }
  }

  async function handleAvatar(file: File | null) {
    if (!file) return;
    setSaving(true);
    setMessage(null);
    try {
      const avatarUrl = await uploadAvatar(profile.id, file);
      await onUpdateProfile({ avatar_url: avatarUrl });
      setMessage('Avatar updated.');
    } catch (error) {
      setMessage(error instanceof Error ? error.message : 'Could not upload avatar.');
    } finally {
      setSaving(false);
    }
  }

  return (
    <div className="fixed inset-0 z-50 grid place-items-center bg-black/70 px-3 py-5 backdrop-blur-md">
      <section className="relay-scrollbar max-h-full w-full max-w-4xl overflow-y-auto rounded-2xl border border-white/10 bg-ink-850 shadow-panel">
        <header className="sticky top-0 z-10 flex items-center justify-between border-b border-white/10 bg-ink-850/95 px-5 py-4 backdrop-blur">
          <div className="flex items-center gap-3">
            <div className="grid h-10 w-10 place-items-center rounded-xl border border-relay-cyan/30 bg-relay-cyan/10 text-relay-cyan">
              <Settings size={19} />
            </div>
            <div>
              <h2 className="text-lg font-bold text-white">Settings</h2>
              <p className="text-xs text-slate-500">Profile, theme, notifications, and app preferences.</p>
            </div>
          </div>
          <button
            type="button"
            onClick={onClose}
            className="grid h-10 w-10 place-items-center rounded-xl text-slate-400 transition hover:bg-white/10 hover:text-white"
            aria-label="Close settings"
          >
            <X size={19} />
          </button>
        </header>

        <div className="grid gap-5 p-5 lg:grid-cols-[0.9fr_1.1fr]">
          <section className="overflow-hidden rounded-xl border border-white/10 bg-white/[0.035]">
            <div className="h-28 bg-[radial-gradient(circle_at_25%_25%,rgba(34,211,238,.38),transparent_30%),linear-gradient(135deg,rgba(79,140,255,.75),rgba(139,92,246,.42),rgba(5,7,13,.95))]" />
            <div className="-mt-9 px-5 pb-5">
              <label className="relative inline-block cursor-pointer">
                <Avatar name={profile.display_name} src={profile.avatar_url} online size="lg" />
                <span className="absolute -bottom-1 -right-1 grid h-8 w-8 place-items-center rounded-xl border border-white/10 bg-ink-750 text-relay-cyan shadow-panel">
                  <Camera size={15} />
                </span>
                <input
                  type="file"
                  accept="image/*"
                  className="hidden"
                  onChange={(event) => handleAvatar(event.target.files?.[0] ?? null)}
                />
              </label>

              <div className="mt-4 space-y-3">
                <label className="block">
                  <span className="text-xs font-semibold uppercase tracking-[0.16em] text-slate-500">Display name</span>
                  <input
                    value={displayName}
                    onChange={(event) => setDisplayName(event.target.value)}
                    className="mt-2 w-full rounded-xl border border-white/10 bg-black/25 px-3 py-2.5 text-sm text-white"
                    placeholder="Display name"
                  />
                </label>
                <label className="block">
                  <span className="text-xs font-semibold uppercase tracking-[0.16em] text-slate-500">Username</span>
                  <input
                    value={username}
                    onChange={(event) => setUsername(event.target.value)}
                    className="mt-2 w-full rounded-xl border border-white/10 bg-black/25 px-3 py-2.5 text-sm text-white"
                    placeholder="Username"
                  />
                </label>
                <label className="block">
                  <span className="text-xs font-semibold uppercase tracking-[0.16em] text-slate-500">Description</span>
                  <textarea
                    value={bio}
                    onChange={(event) => setBio(event.target.value.slice(0, 280))}
                    rows={4}
                    className="relay-scrollbar mt-2 w-full resize-none rounded-xl border border-white/10 bg-black/25 px-3 py-2.5 text-sm leading-6 text-white placeholder:text-slate-600"
                    placeholder="Tell people what you are working on, your status, or a short intro."
                  />
                  <span className="mt-1 block text-right text-[11px] text-slate-600">{bio.length}/280</span>
                </label>
                <button
                  type="button"
                  onClick={saveProfile}
                  disabled={saving}
                  className="w-full rounded-xl bg-white px-4 py-2.5 text-sm font-bold text-ink-950 transition hover:brightness-110 disabled:opacity-50"
                >
                  {saving ? 'Saving...' : 'Save profile'}
                </button>
                {message && <p className="text-xs text-relay-cyan">{message}</p>}
              </div>
            </div>
          </section>

          <section className="space-y-5">
            <div className="rounded-xl border border-white/10 bg-white/[0.035] p-4">
              <div className="mb-4 flex items-center gap-2">
                <Moon size={17} className="text-relay-cyan" />
                <h3 className="text-sm font-bold text-white">Theme</h3>
              </div>
              <div className="grid gap-3 sm:grid-cols-2">
                {themes.map((theme) => (
                  <button
                    key={theme.id}
                    type="button"
                    onClick={() => updatePreference({ theme: theme.id })}
                    className={clsx(
                      'rounded-xl border p-3 text-left transition hover:-translate-y-0.5 hover:bg-white/[0.06]',
                      preferences.theme === theme.id
                        ? 'border-relay-cyan/50 bg-relay-cyan/10'
                        : 'border-white/10 bg-black/15',
                    )}
                  >
                    <span className={clsx('mb-3 block h-9 rounded-lg bg-gradient-to-r', theme.swatch)} />
                    <span className="flex items-center justify-between gap-2 text-sm font-semibold text-white">
                      {theme.label}
                      {preferences.theme === theme.id && <Check size={16} className="text-relay-cyan" />}
                    </span>
                    <span className="mt-1 block text-xs leading-5 text-slate-500">{theme.copy}</span>
                  </button>
                ))}
              </div>
            </div>

            <div className="rounded-xl border border-white/10 bg-white/[0.035] p-4">
              <div className="mb-4 flex items-center gap-2">
                <Sparkles size={17} className="text-relay-cyan" />
                <h3 className="text-sm font-bold text-white">Customization</h3>
              </div>
              <div className="space-y-3">
                {[
                  ['Compact message density', 'Fit more channels and messages on screen.', 'compactMode'],
                  ['Glass panels', 'Use translucent panels and deeper blur.', 'glassPanels'],
                  ['Reduce motion', 'Minimize transitions and animation.', 'reduceMotion'],
                ].map(([label, copy, key]) => (
                  <label
                    key={key}
                    className="flex cursor-pointer items-center justify-between gap-4 rounded-xl border border-white/10 bg-black/15 p-3"
                  >
                    <span>
                      <span className="block text-sm font-semibold text-white">{label}</span>
                      <span className="mt-1 block text-xs text-slate-500">{copy}</span>
                    </span>
                    <input
                      type="checkbox"
                      checked={Boolean(preferences[key as keyof RelayPreferences])}
                      onChange={(event) => updatePreference({ [key]: event.target.checked } as Partial<RelayPreferences>)}
                      className="h-5 w-5 accent-cyan-400"
                    />
                  </label>
                ))}
              </div>
            </div>

            <div className="flex flex-col gap-3 rounded-xl border border-white/10 bg-white/[0.035] p-4 sm:flex-row sm:items-center sm:justify-between">
              <div>
                <h3 className="text-sm font-bold text-white">Notifications</h3>
                <p className="mt-1 text-xs text-slate-500">
                  Status: {notificationPermission === 'unsupported' ? 'not supported' : notificationPermission}
                </p>
              </div>
              <button
                type="button"
                onClick={onRequestNotifications}
                className="rounded-xl border border-relay-cyan/30 bg-relay-cyan/10 px-4 py-2 text-sm font-bold text-relay-cyan transition hover:bg-relay-cyan/15"
              >
                Enable notifications
              </button>
            </div>

            <button
              type="button"
              onClick={onSignOut}
              className="inline-flex w-full items-center justify-center gap-2 rounded-xl border border-relay-red/30 bg-relay-red/10 px-4 py-3 text-sm font-bold text-relay-red transition hover:bg-relay-red/15"
            >
              <LogOut size={17} />
              Sign out
            </button>
          </section>
        </div>
      </section>
    </div>
  );
}
